"use client";

/**
 * The car listing card.
 *
 * A car site is not a boutique grid with cars in it. Four things differ from
 * every other card on the platform, and each is deliberate:
 *
 *  - A LANDSCAPE photo. The square retail crop cuts a car in half.
 *  - The title starts with the YEAR — "2018 Toyota Harrier" — because that
 *    is how the trade writes it and how buyers search.
 *  - A SPEC STRIP of the four things a buyer checks in half a second:
 *    mileage, gearbox, fuel, engine. Values, not labels.
 *  - A hire car leads with its class and day rate instead of a price, and
 *    ends in "Check dates" rather than a cart.
 */

import Link from "next/link";
import { Camera, Fuel, Gauge, Settings2, Users } from "lucide-react";
import type { ShopProduct } from "@/lib/types/shop";
import {
  VEHICLE_PLACEHOLDER_IMAGE,
  conditionLabel,
  isForHire,
  isForSale,
  pickLabel,
  TRANSMISSIONS,
  VEHICLE_CLASSES,
  vehicleTitle,
} from "@/lib/config/motors";

const money = (n: number) => `UGX ${Math.round(n).toLocaleString()}`;
const NEW_ARRIVAL_DAYS = 14;

export function MotorsShopProductCard({
  product,
  className,
  productUrlPrefix = "",
}: {
  product: ShopProduct & Record<string, any>;
  className?: string;
  productUrlPrefix?: string;
}) {
  const m = product.motors || {};
  const hireOnly = isForHire(product) && !isForSale(product);
  const bothWays = isForHire(product) && isForSale(product);

  const images: string[] = Array.isArray(product.images) && product.images.length
    ? product.images
    : product.imageUrl
      ? [product.imageUrl]
      : [VEHICLE_PLACEHOLDER_IMAGE];

  const listedAt = Number(m.listedAt || 0);
  const isNew = listedAt > 0 && Date.now() - listedAt < NEW_ARRIVAL_DAYS * 86400000;
  const sold = m.status === "sold";
  const inTransit = m.status === "in_transit" || m.status === "in_bond";

  const ribbon = sold
    ? { text: "SOLD", tone: "#64748b" }
    : inTransit
      ? { text: "IN TRANSIT", tone: "#6366f1" }
      : hireOnly
        ? { text: "FOR HIRE", tone: "#16a34a" }
        : isNew
          ? { text: "NEW ARRIVAL", tone: "#dc2626" }
          : null;

  const href = `${productUrlPrefix}/product/${product.slug || product._id}`;
  const dayRate = Number(m.hire?.dailyRate) || 0;

  // The four values a buyer reads without thinking. A hire card swaps
  // mileage for seats, because nobody hires a car on its odometer.
  const specs = hireOnly
    ? [
        { icon: Users, value: m.seats ? String(m.seats) : "—", key: "seats" },
        { icon: Settings2, value: shortGear(m.transmission), key: "gear" },
        { icon: Fuel, value: cap(m.fuel), key: "fuel" },
        { icon: Gauge, value: m.hire?.mileageCapKm ? `${m.hire.mileageCapKm}km` : "—", key: "per day" },
      ]
    : [
        { icon: Gauge, value: m.odometerKm ? `${Math.round(m.odometerKm / 1000)}k` : "—", key: "km" },
        { icon: Settings2, value: shortGear(m.transmission), key: "gear" },
        { icon: Fuel, value: cap(m.fuel), key: "fuel" },
        { icon: Settings2, value: m.engineCc ? String(m.engineCc) : "—", key: "cc" },
      ];

  return (
    <Link
      href={href}
      className={`group block overflow-hidden rounded-xl border bg-white transition-all hover:shadow-lg dark:bg-neutral-900 ${className || ""}`}
      style={{ borderColor: "rgba(0,0,0,0.08)", opacity: sold ? 0.72 : 1 }}
    >
      {/* Landscape, always — a square crop cuts a car in half */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={images[0]}
          alt={vehicleTitle(product)}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        {ribbon && (
          <span
            className="absolute left-0 top-3 rounded-r px-2.5 py-1 text-[10px] font-bold tracking-widest text-white"
            style={{ backgroundColor: ribbon.tone }}
          >
            {ribbon.text}
          </span>
        )}
        {images.length > 1 && (
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            <Camera className="h-3 w-3" /> {images.length}
          </span>
        )}
      </div>

      <div className="p-3.5">
        <h3 className="line-clamp-1 text-[15px] font-bold leading-tight text-neutral-900 dark:text-neutral-50">
          {hireOnly && m.class ? `${pickLabel(VEHICLE_CLASSES, m.class)} · ${m.make} ${m.model}` : vehicleTitle(product)}
        </h3>

        {hireOnly ? (
          <>
            <p className="mt-1.5 text-xl font-extrabold tabular-nums text-neutral-900 dark:text-neutral-50">
              {money(dayRate)}{" "}
              <span className="text-xs font-medium text-neutral-500">per day</span>
            </p>
            {Number(m.hire?.withDriverRate) > 0 && (
              <p className="text-[12px] font-semibold text-emerald-600 dark:text-emerald-400">
                With driver +{money(Number(m.hire.withDriverRate))}/day
              </p>
            )}
          </>
        ) : (
          <>
            <p className="mt-1.5 text-xl font-extrabold tabular-nums text-neutral-900 dark:text-neutral-50">
              {money(Number(product.price) || 0)}{" "}
              {m.negotiable !== false && <span className="text-xs font-medium text-neutral-500">negotiable</span>}
            </p>
            {bothWays && dayRate > 0 && (
              <p className="text-[12px] font-semibold text-sky-600 dark:text-sky-400">
                or hire from {money(dayRate)}/day
              </p>
            )}
          </>
        )}

        {/* The spec strip */}
        <div className="mt-3 flex border-y border-neutral-200 dark:border-neutral-800">
          {specs.map((s, i) => (
            <div
              key={s.key}
              className={`flex-1 py-2 text-center ${i < specs.length - 1 ? "border-r border-neutral-200 dark:border-neutral-800" : ""}`}
            >
              <p className="text-[12px] font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{s.value}</p>
              <p className="text-[9px] uppercase tracking-wide text-neutral-500">{s.key}</p>
            </div>
          ))}
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-2">
          {m.condition ? (
            <span className="rounded-full border px-2 py-0.5 text-[11px] text-neutral-600 dark:border-neutral-700 dark:text-neutral-300" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
              {conditionLabel(m.condition)}
              {m.auctionGrade ? ` · Grade ${m.auctionGrade}` : ""}
            </span>
          ) : (
            <span />
          )}
          <span className="text-[12px] font-semibold text-sky-600 dark:text-sky-400">
            {hireOnly ? "Check dates" : "View details"}
          </span>
        </div>
      </div>
    </Link>
  );
}

function shortGear(t: string | undefined): string {
  if (!t) return "—";
  if (t === "automatic") return "Auto";
  if (t === "manual") return "Manual";
  return pickLabel(TRANSMISSIONS, t);
}

function cap(s: string | undefined): string {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default MotorsShopProductCard;
